import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import {
	Header,
	Main,
	RootContainer,
	SideBar,
	SideBarContainer,
} from './container.styles'

// Assets & Components
import Logo from '../../../src/assets/img/FullLogo_Transparent.png'
import MenuItems from './menu-item.component'
import Footer from '../Footer/footer.component'

// Redux & API
import { logoutAction } from '../../redux/auth'
import { logout } from '../../api/auth'
import { useAuthenticatedFetch } from '../../hooks'
import {
	FaCartPlus, // For 'Passer Commande'
	FaCalendarAlt, // For 'Historique'
	FaUserCircle, // For 'Mon compte'
	FaFileAlt
} from 'react-icons/fa'
import {
	IoReceiptOutline, // For 'Commandes'
	IoStatsChart, // For 'Statistiques'
	IoLogOut, // For 'Déconnexion'
} from 'react-icons/io5'
import { MdCancel } from 'react-icons/md' // For 'Annulées'
import { GiTakeMyMoney } from 'react-icons/gi' // Already used for 'Prélèvements'
import ChatSupportWidget from '../ChatSupportWidget/ChatSupportWidget'
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from 'react-i18next'
import { MenuItem, Menu } from '@mui/material'
import ReactCountryFlag from "react-country-flag"
// Default User Logo URL
const DEFAULT_USER_LOGO =
	'https://secure.gravatar.com/avatar/6ee7af2da22737fd49ce56adc226d2cb?size=200&d=mp'

function Container({ children }) {
	const history = useHistory()
	const dispatch = useDispatch()
	const user = useSelector((state) => state.auth.user)
	const token = useSelector((state) => state.auth.token)
	const refreshToken = useSelector((state) => state.auth.refreshToken)
	const [userLogoUrl, setUserLogoUrl] = useState(DEFAULT_USER_LOGO)
	const { fetchResource, error, loading } = useAuthenticatedFetch()
	const [activeMenuItem, setActiveMenuItem] = useState(null)

	// Fetch user logo
	useEffect(() => {
		let isMounted = true
		const fetchUserLogo = async () => {
			if (user?.logo?.path) {
				try {
					const result = await fetchResource(user.logo.path, token)
					if (isMounted && result) {
						// Assuming fetchResource returns a usable URL (Blob URL, Base64)
						setUserLogoUrl(result)
					} else if (isMounted) {
						setUserLogoUrl(DEFAULT_USER_LOGO)
					}
				} catch (fetchError) {
					console.error('Error fetching user logo:', fetchError)
					if (isMounted) {
						setUserLogoUrl(DEFAULT_USER_LOGO)
					}
				}
			} else {
				if (isMounted) {
					setUserLogoUrl(DEFAULT_USER_LOGO)
				}
			}
		}

		fetchUserLogo()

		return () => {
			isMounted = false
			// Optional: Revoke Blob URL if created
			// if (userLogoUrl && userLogoUrl.startsWith('blob:')) {
			//  URL.revokeObjectURL(userLogoUrl);
			// }
		}
	}, [user, token, fetchResource]) // Added user to dependency array

	// Handle fetch errors (optional logging)
	useEffect(() => {
		if (error) {
			console.error('Authenticated fetch error:', error)
		}
	}, [error])

	// Determine active menu item based on route
	useEffect(() => {
		const path = history.location.pathname

		if (path.includes('/commandes/new')) {
			setActiveMenuItem('passer-commande')
		} else if (path.includes('/mission-templates')) {
			setActiveMenuItem('mission-templates')
		} else if (path.includes('/commandes/list')) {
			setActiveMenuItem('commandes')
		} else if (path.includes('/historique/client')) {
			setActiveMenuItem('historique')
		} else if (path.includes('/annulées/client')) {
			setActiveMenuItem('annulées')
		} else if (path.includes('/stats/client')) {
			setActiveMenuItem('stats')
		} else if (path.includes('/client/account')) {
			setActiveMenuItem('compte')
		} else if (path.includes('/client/prelevement')) {
			// Add condition for premium feature
			setActiveMenuItem('prelevements')
		} else {
			setActiveMenuItem(null) // Reset if no match
		}
	}, [history.location.pathname])

	const handleLogout = async () => {
		try {
			const data = {
				userId: user?._id, // Check if user exists
				refreshToken,
			}
			if (data.userId && refreshToken) {
				// Ensure data is valid
				await logout(data, token)
			}
			dispatch(logoutAction())
			history.push('/login')
		} catch (logoutError) {
			console.error('Logout error:', logoutError)
			// Still attempt local logout even if API fails
			dispatch(logoutAction())
			history.push('/login')
		}
	}

	// Style constants for icons
	const iconSize = 20
	const iconColor = '#f5f6f7' // Original light grey/off-white
	const whiteIconColor = 'white'
	const logoutIconColor = '#e4eaf0' // Specific color for logout



	const savedLanguage = localStorage.getItem('language') ? localStorage.getItem('language') : 'FR';
	const [selectedItem, setSelectedItem] = useState(savedLanguage === 'FR' ? 1 : 2)
	const { i18n } = useTranslation();
	const [anchorEl, setAnchorEl] = React.useState(null);
	const open = Boolean(anchorEl);
	const handleMenuOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleLanguageMenuItemClick = (event, value) => {
		var language = value === 1 ? "FR" : "EN"
		event.stopPropagation();
		setSelectedItem(value);
		localStorage.setItem('language', language);
		i18n.changeLanguage(language.toLowerCase());
		setAnchorEl(null);

	};

	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

	useEffect(() => {
		const checkIfMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};
		window.addEventListener('resize', checkIfMobile);

		return () => window.removeEventListener('resize', checkIfMobile);
	}, []);

	useEffect(() => {
		if (isMobileMenuOpen && isMobile) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}
	}, [isMobileMenuOpen, isMobile]);

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	};
	const handleMenuItemClick = (originalOnClick) => {
		return () => {
			if (originalOnClick) originalOnClick();
			if (isMobile) setIsMobileMenuOpen(false);
		};
	};
	const { t } = useTranslation()
	return (
		<RootContainer>
			<Header>
				<div style={{ display: 'flex', alignItems: 'center' }}>

					{isMobile && (
						<button
							onClick={toggleMobileMenu}
							style={{
								background: 'none',
								border: 'none',
								cursor: 'pointer',
								padding: '0.5rem',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								marginRight: '1rem',
								color : "white"
							}}
						>
							<MenuIcon size={24}  />
						</button>
					)}

					{!isMobile && <img
						style={{
							width: isMobile ? '8rem' : '12rem',
						}}
						alt="logo"
						src={Logo}
					/>}
				</div>

				<div
					style={{
						background: isMobile ? 'transparent' : 'white',
						paddingRight: isMobile ? '1rem' : '2rem',
						paddingLeft: isMobile ? '1rem' : '2rem',
						paddingTop: '0.5rem',
						paddingBottom: '0.5rem',
						borderRadius: '0.9rem',
						display: 'flex',
						alignItems: 'center',
						gap: '10px', // Added gap for spacing
						fontFamily: 'Montserrat, sans-serif', // Added fallback font
						color: isMobile ? '#fff' : '#72c5ed',
						fontWeight: 'bold',
					}}
				>
					<span>
						{isMobile ? '' : t("welcome")}
						{user && ( // Simplified conditional rendering
							<>
								{user.first_name} {user.last_name}
							</>
						)}
					</span>

					{/* Removed spacer span, using gap now */}

					<img
						style={{
							width: '2rem',
							height: '2rem',
							borderRadius: '50%', // Perfect circle
							objectFit: 'cover', // Ensure image covers the area nicely
						}}
						alt='user'
						src={userLogoUrl}
						onError={(e) => {
							e.target.src = DEFAULT_USER_LOGO
						}} // Fallback if image fails to load
					/>
				</div>
			</Header>
			<div
				style={{
					width: '100%',
					display: 'flex',
					flexDirection: 'row',
					height: 'calc(100vh - 7rem)',
					position: 'relative',
				}}
			>
				{/* Mobile Overlay */}
				{isMobile && isMobileMenuOpen && (
					<div
						onClick={toggleMobileMenu}
						style={{
							position: 'fixed',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							background: 'rgba(0, 0, 0, 0.5)',
							zIndex: 998,
						}}
					/>
				)}
				<div
					style={{
						// Removed background: 'red' (assuming it was for debugging)
						height: 'calc(100vh - 7rem)', // Adjust height if Header height changes
						width: '100%',
						display: 'flex',
						flexDirection: 'row',
						overflow: 'hidden', // Prevent outer div from scrolling
					}}
				>
					<div
						style={{
							position: isMobile ? 'fixed' : 'relative',
							left: isMobile ? (isMobileMenuOpen ? 0 : '-100%') : 0,
							top: isMobile ? 0 : 'auto',
							height: isMobile ? '100vh' : 'auto',
							transition: 'left 0.3s ease-in-out',
							zIndex: 999,
							width: isMobile ? '280px' : 'auto',
							overflowY: 'auto',
						}}
					>
						<SideBar>
							<SideBarContainer>
								<MenuItems
									title={t("place_order")}
									onClick={handleMenuItemClick(() => history.push('/commandes/new'))}
									active={activeMenuItem === 'passer-commande'}
									icon={
										<FaCartPlus size={iconSize} color={iconColor} />
									} // --- Replaced SVG ---
								/>
								<MenuItems
									title={t("mission_templates")}
									onClick={handleMenuItemClick(() => history.push('/mission-templates'))}
									active={activeMenuItem === 'mission-templates'}
									icon={
										<FaFileAlt
											size={iconSize}
											color={whiteIconColor}
										/>
									}
								/>

								<MenuItems
									title={t("orders")}
									onClick={handleMenuItemClick(() => history.push('/commandes/list'))}
									active={activeMenuItem === 'commandes'}
									icon={
										<IoReceiptOutline
											size={iconSize}
											color={whiteIconColor}
										/>
									} // --- Replaced SVG ---
								/>

								<MenuItems
									title={t("history")}
									onClick={handleMenuItemClick(() => history.push('/historique/client'))}
									active={activeMenuItem === 'historique'}
									icon={
										<FaCalendarAlt
											size={iconSize}
											color={iconColor}
										/>
									} // --- Replaced SVG ---
								/>

								<MenuItems
									title={t("cancelled_p")}
									onClick={handleMenuItemClick(() => history.push('/annulées/client'))}
									active={activeMenuItem === 'annulées'}
									icon={
										<MdCancel
											size={iconSize}
											color={whiteIconColor}
										/>
									} // --- Replaced SVG ---
								/>

								<MenuItems
									title={t("statistics")}
									onClick={handleMenuItemClick(() => history.push('/stats/client'))}
									active={activeMenuItem === 'stats'}
									icon={
										<IoStatsChart
											size={iconSize}
											color={iconColor}
										/>
									} // --- Replaced SVG ---
								/>

								{user?.premium && ( // Check if user exists before accessing premium
									<MenuItems
										onClick={handleMenuItemClick(() => history.push('/client/prelevement'))}
										title={t("collections")}
										active={activeMenuItem === 'prelevements'} // Add active state
										icon={
											<GiTakeMyMoney
												style={{
													// Keep specific styling if needed
													fontSize: 22,
													color: whiteIconColor,
												}}
											/>
										}
									/>
								)}

								<MenuItems
									title={t("my_account")}
									onClick={handleMenuItemClick(() => history.push('/client/account'))}
									active={activeMenuItem === 'compte'}
									icon={
										<FaUserCircle
											size={iconSize}
											color={iconColor}
										/>
									} // --- Replaced SVG ---
								/>
								<div className='menu-item-container'>
									<div
										className='menu-item'
										onClick={handleMenuOpen}
										id="language-button"
										aria-controls={open ? 'language-menu' : undefined}
										aria-haspopup="true"
										aria-expanded={open ? 'true' : undefined}
									>
										<div className='icon-container'>
											<ReactCountryFlag
												countryCode={selectedItem === 1 ? "FR" : "US"}
												svg
												style={{ width: '24px', height: '24px' }}
											/>
										</div>
										<span className='menu-title'>
											{selectedItem === 1 ? "Français" : "English"}
										</span>
									</div>
								</div>

								<Menu
									id="language-menu"
									anchorEl={anchorEl}
									open={open}
									onClose={handleMenuClose}
									slotProps={{
										list: {
											'aria-labelledby': 'language-button',
										},
									}}
								>
									<MenuItem
										onClick={(event) => handleLanguageMenuItemClick(event, 1)}
										style={{ display: 'flex', gap: 8 }}
									>
										<ReactCountryFlag countryCode="FR" svg />
										Français
									</MenuItem>
									<MenuItem
										onClick={(event) => handleLanguageMenuItemClick(event, 2)}
										style={{ display: 'flex', gap: 8 }}
									>
										<ReactCountryFlag countryCode="US" svg />
										English
									</MenuItem>
								</Menu>
								<MenuItems
									onClick={handleMenuItemClick(handleLogout)}
									title={t("logout")}
									// No active state for logout
									icon={
										<IoLogOut
											size={iconSize}
											color={logoutIconColor}
										/>
									} // --- Replaced SVG ---
								/>

							</SideBarContainer>
						</SideBar>
					</div>
					{/* Main Content Area */}
					<div
						style={{
							width: '100%',
							height: '100%', // Fill the container div
							background: 'white',
							overflowY:
								user?.status === 'suspendu' ? 'hidden' : 'auto', // Check user exists
							position: 'relative', // Needed for absolute positioning of overlay
						}}
					>
						{children}

						{/* Suspended Account Overlay */}
						{user?.status === 'suspendu' && ( // Check user exists
							<div
								style={{
									position: 'absolute',
									inset: 0, // Covers the entire parent div
									background: 'rgba(0, 0, 0, 0.6)', // Darker overlay
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									zIndex: 10, // Ensure overlay is on top
									padding: '1rem', // Add padding for smaller screens
								}}
							>
								<div
									style={{
										fontSize: isMobile ? 18 : 20, // Responsive width
										background: 'white',
										borderRadius: '8px', // Slightly rounded corners
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'center',
										padding: '2rem',
										textAlign: 'center',
										boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', // Add shadow for depth
									}}
								>
									<h2 // Use heading for semantic importance
										style={{
											fontFamily: 'Montserrat, sans-serif',
											fontSize: isMobile ? 18 : 20,
											marginBottom: '1.5rem', // More space
											color: '#dc3545', // Red color for warning
										}}
									>
										{t("suspended_account")}
									</h2>
									<p // Use paragraph for text content
										style={{
											fontFamily: 'Montserrat, sans-serif',
											fontSize: isMobile ? 14 : 16,
											lineHeight: 1.6, // Improve readability
										}}
									>
										{t("suspended_account_msg")}
									</p>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			<ChatSupportWidget />
			<Footer />
		</RootContainer>
	)
}

export default Container
